import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/core/api/client";
import type { Role } from "@/core/auth/types";
import type { Season } from "@/core/seasons/api";

export interface OidcConfig {
  enabled: boolean;
  name: string;
  client_id: string;
  server_url: string;
  scopes: string[];
  groups_claim: string;
  is_configured: boolean;
  secret_hint: string;
  updated_at: string;
}
export interface GroupMapping {
  id: number;
  claim_value: string;
  role: Role;
}
export interface TenantSettings {
  name: string;
  branding: {
    display_name: string;
    logo_url: string;
    primary_color: string;
    accent_color: string;
    background_color: string;
  };
  locale_default: string;
  timezone: string;
  cadence_cron: string;
  live_feedback_enabled: boolean;
  notifications_enabled: boolean;
  solution_readback_enabled: boolean;
  /** Games offered, in the games page's order; empty is games off (ADR 0075). */
  enabled_games: string[];
}
export interface AdminUser {
  id: number;
  email: string;
  display_name: string;
  roles: Role[];
  has_password_login: boolean;
  is_active: boolean;
  date_joined: string;
}
interface Page<T> {
  results: T[];
  count: number;
}

export function useOidcConfig() {
  return useQuery({
    queryKey: ["admin", "oidc"],
    queryFn: () => api<OidcConfig>("/api/auth/admin/oidc/"),
  });
}
export function useSaveOidcConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<OidcConfig> & { client_secret?: string }) =>
      api<OidcConfig>("/api/auth/admin/oidc/", { method: "PUT", json: body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "oidc"] });
      void qc.invalidateQueries({ queryKey: ["tenant"] });
    },
  });
}
export function useGroupMappings() {
  return useQuery({
    queryKey: ["admin", "group-mappings"],
    queryFn: () => api<GroupMapping[]>("/api/auth/admin/oidc/group-mappings/"),
  });
}
export function useCreateGroupMapping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Omit<GroupMapping, "id">) =>
      api<GroupMapping>("/api/auth/admin/oidc/group-mappings/", { method: "POST", json: body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "group-mappings"] }),
  });
}
export function useDeleteGroupMapping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api<void>(`/api/auth/admin/oidc/group-mappings/${id}/`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "group-mappings"] }),
  });
}
export function useTenantSettings() {
  return useQuery({
    queryKey: ["admin", "tenant-settings"],
    queryFn: () => api<TenantSettings>("/api/tenant/settings/"),
  });
}
export function useSaveTenantSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<TenantSettings>) =>
      api<TenantSettings>("/api/tenant/settings/", { method: "PUT", json: body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "tenant-settings"] });
      void qc.invalidateQueries({ queryKey: ["tenant"] });
    },
  });
}
export function useSendTestEmail() {
  return useMutation({
    mutationFn: () =>
      api<{ sent_to: string }>("/api/tenant/notifications/test-email/", { method: "POST" }),
  });
}
export function useAdminUsers(q: string) {
  return useQuery({
    queryKey: ["admin", "users", q],
    queryFn: () => api<Page<AdminUser>>(`/api/auth/admin/users/?q=${encodeURIComponent(q)}`),
  });
}
export function useUpdateUserRoles() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, roles }: { id: number; roles: Role[] }) =>
      api<AdminUser>(`/api/auth/admin/users/${id}/`, { method: "PATCH", json: { roles } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "users"] }),
  });
}
export interface LocalUserInput {
  email: string;
  display_name: string;
  password: string;
  roles: Role[];
}
/** Creates a local account: signs in with a password, holds only the roles given (ADR 0027). */
export function useCreateLocalUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: LocalUserInput) =>
      api<AdminUser>("/api/auth/admin/users/", { method: "POST", json: body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "users"] }),
  });
}

export interface SeasonInput {
  name: string;
  starts_at: string;
  ends_at: string;
  scoring: string;
  puzzle_slugs: string[];
}
const SEASON_KEYS = [["admin", "seasons"], ["seasons"]] as const;
function invalidateSeasons(qc: ReturnType<typeof useQueryClient>) {
  for (const queryKey of SEASON_KEYS) void qc.invalidateQueries({ queryKey: [...queryKey] });
}
export function useAdminSeasons() {
  return useQuery({
    queryKey: ["admin", "seasons"],
    queryFn: () => api<Season[]>("/api/admin/seasons/"),
  });
}
export function useCreateSeason() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SeasonInput) =>
      api<Season>("/api/admin/seasons/", { method: "POST", json: body }),
    onSuccess: () => invalidateSeasons(qc),
  });
}
export function useUpdateSeason() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: SeasonInput & { id: number }) =>
      api<Season>(`/api/admin/seasons/${id}/`, { method: "PATCH", json: body }),
    onSuccess: () => invalidateSeasons(qc),
  });
}
export function useDeleteSeason() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api<void>(`/api/admin/seasons/${id}/`, { method: "DELETE" }),
    onSuccess: () => invalidateSeasons(qc),
  });
}
